import { predictionCardHasRefData } from '@src/providers/predictionEventOutcomes';
import type { PredictionRefDataIndexes } from '@src/providers/predictionRefDataIndexes';
import { flatten, sortBy } from 'lodash-es';
import { useMemo } from 'react';

import { CardViewEnum, type PredictionMarketCardConfig, type Security } from '@talos/kyoko';

export function usePredictionMarketCardColumns(
  sortCards: boolean,
  isAddingCard: boolean,
  columns: PredictionMarketCardConfig[][],
  predictionOutcomesBySymbol: Map<string, Security>,
  refDataIndexes: PredictionRefDataIndexes,
  isRefDataReady: boolean
) {
  return useMemo(() => {
    if (!sortCards || isAddingCard || !isRefDataReady) {
      return columns;
    }

    const lastColumnIndex = columns.reduceRight(
      (result, column, index) => (column.length > 0 ? Math.max(result, index) : result),
      -1
    );
    const numberOfColumns = lastColumnIndex + 1;
    const sortedCards = sortBy(
      flatten(columns).filter(card => predictionCardHasRefData(card, predictionOutcomesBySymbol, refDataIndexes)),
      card => {
        const security = card.outcomeSymbol ? predictionOutcomesBySymbol.get(card.outcomeSymbol) : undefined;
        return [card.eventTitle, security?.DisplaySymbol ?? card.outcomeSymbol ?? ''];
      }
    );

    let columnIndex = 0;
    let cardIndex = 0;
    const sortedColumns: PredictionMarketCardConfig[][] = Array.from({ length: numberOfColumns }, () => []);

    while (cardIndex < sortedCards.length) {
      const column = sortedColumns[columnIndex % numberOfColumns];
      const columnHeight = column.reduce((height, card) => {
        const view = card.view ?? CardViewEnum.NORMAL;
        return height + (view === CardViewEnum.COMPACT ? 1 : view === CardViewEnum.NORMAL ? 2 : 3);
      }, 0);

      if (columnHeight <= Math.floor(columnIndex / numberOfColumns)) {
        column.push(sortedCards[cardIndex]);
        cardIndex += 1;
      }

      columnIndex += 1;
    }

    return sortedColumns;
  }, [columns, isAddingCard, isRefDataReady, predictionOutcomesBySymbol, refDataIndexes, sortCards]);
}
